import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { ArrowUpIcon, ArrowDownIcon, ArrowPathIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, RadarChart, PolarGrid, PolarAngleAxis, Radar } from 'recharts'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { useAppStore } from '../store/useAppStore'
import { generateInsights } from '../lib/groq'
import { clsx } from 'clsx'

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.08 } } }
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const } } }

const AnimatedNumber = ({ value, decimals = 0, prefix = '', suffix = '' }: { value: number; decimals?: number; prefix?: string; suffix?: string }) => {
  const [display, setDisplay] = useState(0)
  useEffect(() => {
    const duration = 1200
    const start = performance.now()
    const animate = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplay(eased * value)
      if (progress < 1) requestAnimationFrame(animate)
    }
    requestAnimationFrame(animate)
  }, [value])
  return <span>{prefix}{display.toFixed(decimals)}{suffix}</span>
}

const kpis = [
  { label: 'Revenue', value: 42800, prefix: '$', suffix: '', decimals: 0, change: 8.3, positive: true, pulseClass: 'pulse-blue' },
  { label: 'Customers', value: 324, prefix: '', suffix: '', decimals: 0, change: 5.1, positive: true, pulseClass: 'pulse-green' },
  { label: 'Conversion', value: 2.4, prefix: '', suffix: '%', decimals: 1, change: 0.3, positive: true, pulseClass: 'pulse-amber' },
  { label: 'Satisfaction', value: 88, prefix: '', suffix: '%', decimals: 0, change: 2.1, positive: true, pulseClass: 'pulse-red' },
]

const revenueData = [
  { month: 'Jan', revenue: 8200, target: 9000, lastYear: 6800 },
  { month: 'Feb', revenue: 9400, target: 9500, lastYear: 7200 },
  { month: 'Mar', revenue: 10600, target: 10000, lastYear: 8100 },
  { month: 'Apr', revenue: 9800, target: 10500, lastYear: 7600 },
  { month: 'May', revenue: 11200, target: 11000, lastYear: 8800 },
  { month: 'Jun', revenue: 12800, target: 12000, lastYear: 9600 },
  { month: 'Jul', revenue: 11900, target: 12500, lastYear: 9200 },
  { month: 'Aug', revenue: 13400, target: 13000, lastYear: 10200 },
  { month: 'Sep', revenue: 14200, target: 13500, lastYear: 11100 },
  { month: 'Oct', revenue: 13800, target: 14000, lastYear: 10800 },
  { month: 'Nov', revenue: 15600, target: 15000, lastYear: 12200 },
  { month: 'Dec', revenue: 16800, target: 16000, lastYear: 13400 },
]

const channelData = [
  { name: 'Email', value: 38, color: '#0c90e9' },
  { name: 'WhatsApp', value: 22, color: '#22c55e' },
  { name: 'Telegram', value: 12, color: '#3b82f6' },
  { name: 'Social', value: 18, color: '#f59e0b' },
  { name: 'Direct', value: 10, color: '#8b5cf6' },
]

const serviceUsageData = [
  { service: 'AI Writer', usage: 72 },
  { service: 'CRM', usage: 58 },
  { service: 'Email Ops', usage: 81 },
  { service: 'Analytics', usage: 45 },
  { service: 'Chatbot', usage: 63 },
  { service: 'Scheduler', usage: 38 },
]

const trafficData = [
  { day: 'Mon', visitors: 420, conversions: 18 },
  { day: 'Tue', visitors: 510, conversions: 24 },
  { day: 'Wed', visitors: 480, conversions: 21 },
  { day: 'Thu', visitors: 590, conversions: 28 },
  { day: 'Fri', visitors: 540, conversions: 25 },
  { day: 'Sat', visitors: 320, conversions: 12 },
  { day: 'Sun', visitors: 280, conversions: 9 },
]

const recentLeads = [
  { name: 'Sarah Chen', email: 'sarah@techcorp.io', stage: 'proposal', value: 12500, source: 'Email', time: '2 min ago' },
  { name: 'Marcus Williams', email: 'marcus@growth.co', stage: 'qualified', value: 8200, source: 'WhatsApp', time: '15 min ago' },
  { name: 'Aisha Patel', email: 'aisha@innovate.com', stage: 'negotiation', value: 24000, source: 'LinkedIn', time: '1 hr ago' },
  { name: 'James O\'Brien', email: 'james@startup.io', stage: 'lead', value: 5400, source: 'Telegram', time: '2 hr ago' },
  { name: 'Elena Rodriguez', email: 'elena@global.co', stage: 'prospect', value: 15800, source: 'Direct', time: '3 hr ago' },
]

const recentActivity = [
  { action: 'New lead captured from Email campaign', time: '5 min ago', type: 'lead' },
  { action: 'Deal closed with TechCorp — $12,500', time: '1 hr ago', type: 'deal' },
  { action: 'AI Writer generated 5 new content pieces', time: '2 hr ago', type: 'ai' },
  { action: 'Customer satisfaction survey sent to 45 contacts', time: '3 hr ago', type: 'survey' },
  { action: 'Monthly revenue report ready for review', time: '5 hr ago', type: 'report' },
]

const fallbackInsights = [
  { title: 'Revenue Trend', description: 'Your revenue has grown 23% over the last quarter. The AI Writer service is driving the most engagement with 90% utilization.', icon: '📈', severity: 'positive' as const },
  { title: 'Conversion Alert', description: 'Conversion rate dipped 0.5% this week. Consider A/B testing your landing page headlines and adjusting email follow-up timing.', icon: '⚠️', severity: 'warning' as const },
  { title: 'Customer Insight', description: 'Customers who use 3+ services have 4x higher retention. Recommend cross-selling Analytics to WhatsApp-only users.', icon: '💡', severity: 'info' as const },
]

const stageColors: Record<string, 'info' | 'warning' | 'primary' | 'success'> = {
  lead: 'info',
  prospect: 'warning',
  qualified: 'primary',
  proposal: 'success',
  negotiation: 'warning',
}

const activityIcons: Record<string, string> = {
  lead: '👤',
  deal: '💰',
  ai: '🤖',
  survey: '📊',
  report: '📄',
}

export function Dashboard() {
  const { user } = useAppStore()
  const firstName = user?.full_name?.split(' ')[0] || 'there'
  const [healthScore, setHealthScore] = useState(0)
  const circumference = 2 * Math.PI * 54
  const healthTarget = 87

  // AI Insights state
  const [aiInsights, setAiInsights] = useState(fallbackInsights)
  const [insightsLoading, setInsightsLoading] = useState(false)

  const fetchInsights = useCallback(async () => {
    setInsightsLoading(true)
    try {
      const context = `Revenue: $42,800 (+8.3% MoM), Customers: 324 (+5.1%), Conversion: 2.4% (+0.3%), Satisfaction: 88% (+2.1%). Revenue data: Jan $8.2k, Feb $9.4k, Mar $10.6k, Apr $9.8k, May $11.2k, Jun $12.8k, Jul $11.9k, Aug $13.4k, Sep $14.2k, Oct $13.8k, Nov $15.6k, Dec $16.8k. Top services: Email Ops (81%), AI Writer (72%), Chatbot (63%), CRM (58%). Recent leads: Sarah Chen (proposal, $12.5k), Marcus Williams (qualified, $8.2k), Aisha Patel (negotiation, $24k).`
      const result = await generateInsights(context)
      const severityIcons = { positive: '📈', warning: '⚠️', info: '💡' }
      setAiInsights(
        result.map((i) => ({
          title: i.title,
          description: i.description,
          icon: severityIcons[i.severity] || '💡',
          severity: i.severity,
        })),
      )
    } catch {
      setAiInsights(fallbackInsights)
    } finally {
      setInsightsLoading(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setHealthScore(healthTarget), 300)
    fetchInsights()
    return () => clearTimeout(timer)
  }, [fetchInsights, healthTarget])

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-6 p-6">
      <motion.div variants={item}>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Welcome back, {firstName}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Here's what's happening with your business today.</p>
      </motion.div>

      <motion.div variants={container} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <motion.div key={kpi.label} variants={item}>
            <Card padding="md" className={clsx('pulse-stat-card', kpi.pulseClass)}>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{kpi.label}</p>
              <div className="flex items-end justify-between mt-2">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  <AnimatedNumber value={kpi.value} decimals={kpi.decimals} prefix={kpi.prefix} suffix={kpi.suffix} />
                </p>
                <span className={clsx('flex items-center text-sm font-medium', kpi.positive ? 'text-green-600' : 'text-red-600')}>
                  {kpi.positive ? <ArrowUpIcon className="w-4 h-4 mr-1" /> : <ArrowDownIcon className="w-4 h-4 mr-1" />}
                  {Math.abs(kpi.change)}%
                </span>
              </div>
              <div className="mt-3 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min((kpi.value / (kpi.label === 'Revenue' ? 50000 : kpi.label === 'Customers' ? 2000 : kpi.label === 'Satisfaction' ? 100 : 10)) * 100, 100)}%` }}
                  transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] as const }}
                  className={clsx(
                    'h-full rounded-full',
                    kpi.positive ? 'bg-green-500' : 'bg-red-500'
                  )}
                />
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div variants={item} className="lg:col-span-2">
          <Card padding="md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Revenue Overview</h2>
              <Badge variant="info">2024</Badge>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0c90e9" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0c90e9" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="targetGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value: any) => [`$${value.toLocaleString()}`, '']} />
                <Legend />
                <Area type="monotone" dataKey="revenue" stroke="#0c90e9" strokeWidth={2} fill="url(#revenueGradient)" />
                <Area type="monotone" dataKey="target" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" fill="url(#targetGradient)" />
                <Area type="monotone" dataKey="lastYear" stroke="#d1d5db" strokeWidth={1} fill="none" strokeDasharray="3 3" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card padding="md" className="h-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Health Score</h2>
              <Badge variant="success">Excellent</Badge>
            </div>
            <div className="flex items-center justify-center my-6">
              <svg width="140" height="140" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                <motion.circle
                  cx="60" cy="60" r="54" fill="none" stroke="#22c55e" strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={circumference}
                  initial={{ strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset: circumference - (healthScore / 100) * circumference }}
                  transition={{ duration: 1.5, delay: 0.5, ease: [0.22, 1, 0.36, 1] as const }}
                  transform="rotate(-90 60 60)"
                />
                <text x="60" y="60" textAnchor="middle" dominantBaseline="central" className="text-3xl font-bold fill-gray-900 dark:fill-white">
                  {healthScore}
                </text>
                <text x="60" y="80" textAnchor="middle" className="text-xs fill-gray-400">out of 100</text>
              </svg>
            </div>
            <div className="space-y-3">
              {serviceUsageData.slice(0, 4).map((s) => (
                <div key={s.service}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400">{s.service}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{s.usage}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${s.usage}%` }}
                      transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
                      className="h-full bg-primary-500 rounded-full"
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div variants={item}>
          <Card padding="md" className="h-full">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Channel Distribution</h2>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={channelData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                  {channelData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => [`${value}%`, 'Share']} />
                <Legend iconType="circle" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card padding="md" className="h-full">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Weekly Traffic</h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={trafficData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip />
                <Legend />
                <Bar dataKey="visitors" fill="#0c90e9" radius={[4, 4, 0, 0]} />
                <Bar dataKey="conversions" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card padding="md" className="h-full">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Service Usage</h2>
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart cx="50%" cy="50%" outerRadius="70%" data={serviceUsageData}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="service" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <Radar name="Usage" dataKey="usage" stroke="#0c90e9" fill="#0c90e9" fillOpacity={0.25} strokeWidth={2} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div variants={item} className="lg:col-span-2">
          <Card padding="md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Leads</h2>
              <Badge variant="primary">{recentLeads.length} new</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Stage</th>
                    <th className="pb-3 font-medium">Value</th>
                    <th className="pb-3 font-medium">Source</th>
                    <th className="pb-3 font-medium text-right">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.map((lead) => (
                    <tr key={lead.email} className="border-b border-gray-50 dark:border-gray-800 last:border-0">
                      <td className="py-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{lead.name}</p>
                          <p className="text-xs text-gray-400">{lead.email}</p>
                        </div>
                      </td>
                      <td className="py-3">
                        <Badge variant={stageColors[lead.stage] || 'info'}>{lead.stage}</Badge>
                      </td>
                      <td className="py-3 font-medium text-gray-900 dark:text-white">${lead.value.toLocaleString()}</td>
                      <td className="py-3 text-gray-500 dark:text-gray-400">{lead.source}</td>
                      <td className="py-3 text-right text-gray-400">{lead.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </motion.div>

        <motion.div variants={item} className="space-y-6">
          <Card padding="md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-primary-500" />
                AI Insights
                <Badge variant="success" size="sm">Groq</Badge>
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchInsights}
                disabled={insightsLoading}
              >
                <ArrowPathIcon className={clsx('w-4 h-4', insightsLoading && 'animate-spin')} />
              </Button>
            </div>
            <div className="space-y-4">
              {aiInsights.map((insight) => (
                <div key={insight.title} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-start gap-3">
                    <span className="text-lg flex-shrink-0">{insight.icon}</span>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{insight.title}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{insight.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {recentActivity.map((act, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-base flex-shrink-0 mt-0.5">{activityIcons[act.type]}</span>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-snug">{act.action}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{act.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  )
}
